import { useEffect } from "react";
import { ListItemButton, styled } from "@mui/material";
import { Feature, Geometry } from "geojson";
import L from "leaflet";
import { useMap } from "react-leaflet";

import { LayerSearchFormValues, LayerProperties } from "types";
import { useSearchSelected } from "hooks/searchHooks";
import { LayerSearchResultItemAttribute } from "components/form/layer/LayerSearchResultItemAttribute";

interface Props {
  config: string;
  selection: LayerSearchFormValues;
  feature: Feature<Geometry, { [key: string]: string }>;
  layerProperties: LayerProperties[];
}

export function LayerSearchResultItem(props: Props) {
  const { config, selection, feature, layerProperties } = props;
  const map = useMap();
  const [{ selected, geometry }, updateSearchSelected] = useSearchSelected();

  function onClick(feature: Feature<Geometry, { [key: string]: string }>) {
    const { id: featureId, geometry } = feature;
    if (featureId) {
      updateSearchSelected(featureId.toString(), geometry);

      // fly to new selected geometry
      const geometryGeojson = L.geoJSON(geometry);
      map.flyToBounds(geometryGeojson.getBounds());
    } else {
      updateSearchSelected(undefined, null);
    }
  }

  useEffect(() => {
    if (
      geometry === null &&
      feature &&
      feature.id &&
      selected === feature.id.toString()
    ) {
      updateSearchSelected(feature.id.toString(), feature.geometry);
    }
  }, [selected, feature, geometry, updateSearchSelected]);

  return (
    <StyledListItem
      onClick={() => onClick(feature)}
      selected={feature.id === selected}
    >
      {selection.attributes.map((attributeKey, i) => {
        return (
          <LayerSearchResultItemAttribute
            key={attributeKey}
            query={selection.query}
            config={config}
            layerId={selection.layer}
            attributeKey={attributeKey}
            value={feature.properties[attributeKey]}
            layerProperties={layerProperties}
          />
        );
      })}
    </StyledListItem>
  );
}

const StyledListItem = styled(ListItemButton)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[700]};
  .attribute-item {
    width: 100%;

    & + .attribute-item {
      padding-top: ${({ theme }) => theme.spacing(2)};
    }
  }

  .attribute-label {
    font-style: italic;
    color: ${({ theme }) => theme.palette.grey[600]};
  }

  .attribute-value p {
    margin: 0;
  }
`;
